import React, { useEffect, useState } from 'react';
import MainLayout from '../components/Layout/MainLayout.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.jsx';
import { Button } from '../components/ui/button.jsx';
import { Input } from '../components/ui/input.jsx';
import { Label } from '../components/ui/label.jsx';
import { User, Building2, Mail, LogOut, Lock, Database, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PasswordField = ({ label, value, onChange, testId, required = true, minLength }) => {
  const [show, setShow] = useState(false);
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="relative">
        <Input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          minLength={minLength}
          required={required}
          data-testid={testId}
          className="pr-10"
        />
        <button
          type="button"
          onClick={() => setShow(s => !s)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
          tabIndex={-1}
          aria-label={show ? 'Ocultar senha' : 'Mostrar senha'}
          data-testid={`${testId}-toggle`}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

const Configuracoes = () => {
  const userRole = localStorage.getItem('userRole') || 'respondent';
  const [company, setCompany] = useState(null);
  const [hasPassword, setHasPassword] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [savingPwd, setSavingPwd] = useState(false);
  const [seedStatus, setSeedStatus] = useState(null);
  const [resetting, setResetting] = useState(false);

  const isG2Admin = (company?.email || '').toLowerCase() === 'g2consultoriarh@gmail.com';

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API}/companies/me`, { headers: { Authorization: `Bearer ${token}` } });
        setCompany(data);
        const { data: pwd } = await axios.post(`${API}/auth/has-password`, { email: data.email });
        setHasPassword(!!pwd.has_password);
        if ((data.email || '').toLowerCase() === 'g2consultoriarh@gmail.com') {
          const { data: status } = await axios.get(`${API}/admin/seed-status`, { headers: { Authorization: `Bearer ${token}` } });
          setSeedStatus(status);
        }
      } catch (e) { console.error(e); }
    };
    load();
  }, []);

  const refreshSeedStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/admin/seed-status`, { headers: { Authorization: `Bearer ${token}` } });
      setSeedStatus(data);
    } catch (e) { /* ignore */ }
  };

  const handleResetSeed = async (removeCompanies) => {
    const msg = removeCompanies
      ? 'Tem certeza? Isso REMOVERÁ as 15 empresas seed E todos os indicadores delas. Esta ação é IRREVERSÍVEL.'
      : 'Confirma limpar TODOS os indicadores das empresas seed? As empresas serão mantidas, apenas indicadores serão apagados.';
    if (!window.confirm(msg)) return;
    setResetting(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/admin/reset-seed-data?remove_companies=${removeCompanies}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Reset concluído. ${JSON.stringify(data.removed)}`);
      refreshSeedStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao resetar');
    } finally { setResetting(false); }
  };

  const roleLabel = { admin: 'Administrador G2', manager: 'Empresa Gestora', respondent: 'Empresa Respondente' };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/login';
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwdForm.new_password.length < 6) { toast.error('A senha deve ter ao menos 6 caracteres'); return; }
    if (pwdForm.new_password !== pwdForm.confirm) { toast.error('Confirmação não confere'); return; }
    setSavingPwd(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/change-password`, {
        current_password: hasPassword ? pwdForm.current_password : null,
        new_password: pwdForm.new_password,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(hasPassword ? 'Senha atualizada' : 'Senha definida com sucesso');
      setHasPassword(true);
      setPwdForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar senha');
    } finally { setSavingPwd(false); }
  };

  return (
    <MainLayout title="Configurações" userRole={userRole}>
      <div className="space-y-6 max-w-3xl" data-testid="configuracoes-page">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4" /> Dados da Empresa</CardTitle>
          </CardHeader>
          <CardContent>
            {company ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div><span className="text-slate-500">Razão Social</span><p className="font-medium text-slate-900 mt-0.5">{company.razao_social}</p></div>
                <div><span className="text-slate-500">CNPJ</span><p className="font-medium text-slate-900 mt-0.5">{company.cnpj}</p></div>
                <div><span className="text-slate-500">Segmento</span><p className="font-medium text-slate-900 mt-0.5">{company.segmento}</p></div>
                <div><span className="text-slate-500">Região</span><p className="font-medium text-slate-900 mt-0.5 capitalize">{company.regiao}</p></div>
                <div><span className="text-slate-500">Porte</span><p className="font-medium text-slate-900 mt-0.5 capitalize">{company.porte}</p></div>
                <div><span className="text-slate-500">Total Funcionários</span><p className="font-medium text-slate-900 mt-0.5">{company.total_funcionarios?.toLocaleString('pt-BR')}</p></div>
              </div>
            ) : (
              <p className="text-sm text-slate-400">Carregando...</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><User className="w-4 h-4" /> Conta</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center justify-between py-2 border-b border-slate-100">
              <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" /><span className="text-slate-600">Email</span></div>
              <span className="font-medium text-slate-900">{company?.email || '-'}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-slate-100">
              <span className="text-slate-600">Perfil de Acesso</span>
              <span className="font-medium text-blue-600">{roleLabel[company?.role] || roleLabel.respondent}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-slate-600">Telefone</span>
              <span className="font-medium text-slate-900">{company?.telefone || '-'}</span>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="password-section">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Lock className="w-4 h-4" />
              {hasPassword ? 'Trocar Senha' : 'Definir Senha'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!hasPassword && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5 mb-4">
                Sua conta ainda não possui senha. Defina uma para fazer login mais rápido (sem precisar de token a cada vez).
              </p>
            )}
            <form onSubmit={handleChangePassword} className="space-y-3 max-w-md">
              {hasPassword && (
                <PasswordField
                  label="Senha atual"
                  value={pwdForm.current_password}
                  onChange={e => setPwdForm(p => ({ ...p, current_password: e.target.value }))}
                  testId="current-password-input"
                />
              )}
              <PasswordField
                label={<>Nova senha <span className="text-slate-400">(mínimo 6 caracteres)</span></>}
                value={pwdForm.new_password}
                onChange={e => setPwdForm(p => ({ ...p, new_password: e.target.value }))}
                minLength={6}
                testId="new-password-input"
              />
              <PasswordField
                label="Confirmar nova senha"
                value={pwdForm.confirm}
                onChange={e => setPwdForm(p => ({ ...p, confirm: e.target.value }))}
                minLength={6}
                testId="confirm-password-input"
              />
              <Button type="submit" disabled={savingPwd} data-testid="save-password-button">
                {savingPwd ? 'Salvando...' : (hasPassword ? 'Atualizar Senha' : 'Definir Senha')}
              </Button>
            </form>
          </CardContent>
        </Card>

        {isG2Admin && (
          <Card className="border-amber-200" data-testid="admin-seed-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2 text-amber-700">
                <Database className="w-4 h-4" /> Gestão de Base (Admin G2)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-slate-600">
                Empresas marcadas como "seed" são dados de demonstração inseridos automaticamente.
                Use para limpar quando a plataforma entrar em operação real.
              </p>

              {seedStatus && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs" data-testid="seed-status">
                  <div className="bg-slate-50 rounded p-2"><p className="text-slate-500">Empresas seed</p><p className="font-bold text-slate-900 text-base">{seedStatus.seed_companies}</p></div>
                  <div className="bg-slate-50 rounded p-2"><p className="text-slate-500">Indic. RH</p><p className="font-bold text-slate-900 text-base">{seedStatus.hr_indicators}</p></div>
                  <div className="bg-slate-50 rounded p-2"><p className="text-slate-500">Indic. ESG</p><p className="font-bold text-slate-900 text-base">{seedStatus.esg_indicators}</p></div>
                  <div className="bg-slate-50 rounded p-2"><p className="text-slate-500">Salários</p><p className="font-bold text-slate-900 text-base">{seedStatus.salary_data}</p></div>
                  <div className="bg-slate-50 rounded p-2"><p className="text-slate-500">Benefícios</p><p className="font-bold text-slate-900 text-base">{seedStatus.benefits_data}</p></div>
                </div>
              )}

              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded p-2.5 text-xs text-amber-800">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>Ações irreversíveis. Recomendado fazer backup do MongoDB antes.</span>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  onClick={() => handleResetSeed(false)}
                  disabled={resetting}
                  data-testid="reset-indicators-only-btn"
                >
                  Apagar apenas indicadores seed
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  onClick={() => handleResetSeed(true)}
                  disabled={resetting}
                  data-testid="reset-full-btn"
                >
                  Apagar empresas seed E indicadores
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-red-100">
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-900 text-sm">Encerrar Sessão</p>
              <p className="text-xs text-slate-500">Sair da plataforma g2statistics</p>
            </div>
            <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={handleLogout} data-testid="logout-settings-button">
              <LogOut className="w-4 h-4 mr-2" /> Sair
            </Button>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default Configuracoes;
